import { EvidenceCircleLoader } from '@/components/ui/evidence-circle-loader';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { type Editor } from '@tiptap/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { type InlineEditActionId, type InlineEditIntent } from './atoms';
import { type BarPosition, computeBarPosition } from './inline-edit-bar-position';
import { inlineEditBarMessages as messages } from './inline-edit-bar.messages';
import { InlineEditPromptInput } from './inline-edit-prompt-input';
import {
  EditReviewRow,
  type InlineFeedbackType,
  InsightReviewRow,
  UnansweredReviewRow,
} from './inline-edit-review-rows';
import { InlineEditStreamingBody } from './inline-edit-streaming-body';
import { InlineEditTranscriptExcerpts } from './inline-edit-transcript-excerpts';
import { useGeneratingLabel } from './use-generating-label';
import { type InlineEditPhase } from './use-inline-selection-edit';

/**
 * Floating card anchored under the inline-edit range, spanning the full width
 * of the editor's text column (selection → Ask Heidi v3): shows generation
 * progress with Stop, then Insert below / Discard / Accept once the
 * tracked-changes diff is ready for review.
 */
export const InlineEditBar = ({
  editor,
  phase,
  intent = 'edit',
  actionId,
  proposedText,
  streamingText,
  resultText,
  isUnanswered = false,
  onAccept,
  onInsertBelow,
  onDiscard,
  onReprompt,
  onStop,
  onViewInTranscript,
  onSubmitPrompt,
  onAskInChat,
  onFeedback,
}: {
  editor: Editor;
  phase: Exclude<InlineEditPhase, 'idle'>;
  /** 'edit' shows Accept/Insert below; 'insight' shows View in Transcript / Close. */
  intent?: InlineEditIntent;
  actionId?: InlineEditActionId | null;
  /** When set (panel presentation / insight), the text renders inside the bar. */
  proposedText?: string | null;
  /** Partial SSE text shown while phase is `generating`. */
  streamingText?: string | null;
  /** The raw response, copyable from the review rows in every presentation. */
  resultText?: string | null;
  onAccept: () => void;
  onInsertBelow: () => void;
  onDiscard: () => void;
  /** Drops the proposed edit and reopens the prompt input to try again. */
  onReprompt: () => void;
  onStop: () => void;
  onViewInTranscript?: () => void;
  onSubmitPrompt?: (question: string) => void;
  /** The inline response couldn't answer — show Ask in chat instead of Accept. */
  isUnanswered?: boolean;
  onAskInChat?: () => void;
  /** Thumbs up / down feedback on the inline response. */
  onFeedback?: (feedback: InlineFeedbackType) => void;
}) => {
  const intl = useIntl();
  const [position, setPosition] = useState<BarPosition | null>(() =>
    computeBarPosition(editor, 0)
  );
  const barRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const isInsight = intent === 'insight';
  const generatingLabel = useGeneratingLabel(phase === 'generating', isInsight);

  const reposition = useCallback(() => {
    const barHeight = barRef.current?.getBoundingClientRect().height ?? 0;
    const next = computeBarPosition(editor, barHeight);
    if (next) setPosition(next);
  }, [editor]);

  const setBarRef = useCallback(
    (node: HTMLDivElement | null) => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      barRef.current = node;
      if (!node) return;

      reposition();
      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(() => reposition());
        observer.observe(node);
        resizeObserverRef.current = observer;
      }
    },
    [reposition]
  );

  useEffect(() => {
    reposition();
    const onViewportChange = () => reposition();
    // capture: true also catches scrolls of the editor's scroll container
    window.addEventListener('scroll', onViewportChange, { capture: true, passive: true });
    window.addEventListener('resize', onViewportChange);
    editor.on('transaction', onViewportChange);
    return () => {
      window.removeEventListener('scroll', onViewportChange, { capture: true });
      window.removeEventListener('resize', onViewportChange);
      editor.off('transaction', onViewportChange);
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, [editor, phase, reposition]);

  // Clicking anywhere outside the card dismisses it while prompting or
  // reviewing (there's no explicit dismiss button in those phases). Generating
  // keeps its Stop affordance and shouldn't vanish on a stray click.
  useEffect(() => {
    if (phase !== 'prompting' && phase !== 'review') return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (barRef.current?.contains(target)) return;
      // Ignore clicks landing in a portaled tooltip/popover the bar spawned
      // (e.g. the copy/feedback tooltips) — those aren't "outside".
      if (target instanceof Element) {
        // Ignore clicks in portaled UI the bar spawned (tooltips) or the
        // selection bubble menu — otherwise mousedown here dismisses the inline
        // bar before Check evidence / another menu action can run.
        if (
          target.closest('[data-radix-popper-content-wrapper]') ||
          target.closest('[data-selection-bubble-menu]')
        ) {
          return;
        }
      }
      onDiscard();
    };
    document.addEventListener('mousedown', handlePointerDown, true);
    return () => document.removeEventListener('mousedown', handlePointerDown, true);
  }, [phase, onDiscard]);

  if (!position) return null;

  const hasStreamingText = !!streamingText?.trim();
  const showStreamingContent = phase === 'generating';
  const showReviewContent = phase === 'review' && !!proposedText;

  return createPortal(
    <div
      ref={setBarRef}
      data-testid="inline-edit-bar"
      style={{
        top: position.top,
        left: position.left,
        // Every phase spans the full text-column width so the bar keeps a
        // constant size as it moves prompting → generating → review, instead
        // of shrinking to its content.
        width: position.maxWidth,
        maxWidth: position.maxWidth,
      }}
      className={cn(
        'fixed z-50 flex flex-col gap-2 border border-border',
        // Same chrome as the bottom floating Ask Heidi bar in every phase.
        'rounded-xl bg-surface px-4 py-3 shadow-chat'
      )}
      onMouseDown={(e) => e.preventDefault()}
    >
      {showStreamingContent && (
        <InlineEditStreamingBody text={streamingText ?? ''} isStreaming />
      )}
      {showReviewContent &&
        (isInsight && actionId === 'view_source' && !isUnanswered ? (
          <InlineEditTranscriptExcerpts text={proposedText ?? ''} />
        ) : (
          <div data-testid="inline-edit-proposed-text">
            <InlineEditStreamingBody text={proposedText ?? ''} isStreaming={false} />
          </div>
        ))}
      <div className="flex items-center justify-between gap-2">
        {phase === 'prompting' ? (
          onSubmitPrompt && <InlineEditPromptInput onSubmit={onSubmitPrompt} />
        ) : phase === 'generating' ? (
          <>
            {hasStreamingText ? (
              <div className="min-w-0 flex-1" aria-hidden />
            ) : (
              <div
                className="flex min-w-0 items-center gap-2"
                data-testid="inline-edit-generating-loader"
              >
                <EvidenceCircleLoader
                  size={20}
                  gooey
                  spread={1.4}
                  formation="square"
                  easing="soft"
                  className="shrink-0 text-sunlight-400"
                />
                <span aria-live="polite" className="truncate text-sm text-text-secondary">
                  {intl.formatMessage(generatingLabel)}
                </span>
              </div>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onStop}
              data-testid="inline-edit-stop"
            >
              {intl.formatMessage(messages.stop)}
            </Button>
          </>
        ) : isUnanswered ? (
          <UnansweredReviewRow onDiscard={onDiscard} onAskInChat={onAskInChat} />
        ) : isInsight ? (
          <InsightReviewRow
            copyText={resultText}
            onDiscard={onDiscard}
            onViewInTranscript={
              actionId === 'view_source' ? onViewInTranscript : undefined
            }
            onFeedback={onFeedback}
          />
        ) : (
          <EditReviewRow
            copyText={resultText}
            onAccept={onAccept}
            onInsertBelow={onInsertBelow}
            onReprompt={onReprompt}
            onFeedback={onFeedback}
          />
        )}
      </div>
    </div>,
    document.body
  );
};
